import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * /about — Static "About AUDIVUE" page.
 * Copy is verbatim from the founder-written brand doc. Matches /terms styling.
 * Email contact intentionally points to courtney@audivue.io (founder direct).
 */
const AboutPage = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'About — AUDIVUE';

    // Lightweight per-page meta so social previews and search snippets describe
    // the page accurately. Cleaned up on unmount.
    const description =
      'AUDIVUE is a home for AI music videos — made to be watched, and made ' +
      'for the people making them. Think Vevo and Spotify, with a little ' +
      'TikTok in the mix.';

    const setMeta = (selector, attr, key, value) => {
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute('content');
      el.setAttribute('content', value);
      return { el, prev, created };
    };

    const metaDesc = setMeta('meta[name="description"]', 'name', 'description', description);
    const ogTitle  = setMeta('meta[property="og:title"]', 'property', 'og:title', 'About AUDIVUE');
    const ogDesc   = setMeta('meta[property="og:description"]', 'property', 'og:description', description);
    const ogType   = setMeta('meta[property="og:type"]', 'property', 'og:type', 'website');
    const ogUrl    = setMeta('meta[property="og:url"]', 'property', 'og:url', 'https://audivue.io/about');
    const twCard   = setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');

    return () => {
      document.title = prevTitle;
      [metaDesc, ogTitle, ogDesc, ogType, ogUrl, twCard].forEach(({ el, prev, created }) => {
        if (created) { el.remove(); return; }
        if (prev !== null) el.setAttribute('content', prev);
      });
    };
  }, []);

  return (
    <div
      className="min-h-screen text-zinc-200"
      style={{ background: '#050505' }}
      data-testid="about-page"
    >
      <div className="max-w-3xl mx-auto px-5 md:px-8 pt-10 md:pt-14 pb-24">
        {/* Header */}
        <div className="flex flex-col items-center mb-10 text-center">
          <Link to="/" className="inline-block mb-6" data-testid="about-home-link">
            <img
              src="/wordmark-tight.png"
              alt="AUDIVUE"
              className="h-6 md:h-7 w-auto object-contain"
            />
          </Link>
          <h1
            className="text-3xl md:text-5xl font-bold tracking-tight text-white"
            data-testid="about-h1"
          >
            About AUDIVUE
          </h1>
          <p className="mt-3 text-xs uppercase tracking-[0.25em] text-orange-400/90">
            Music videos, reborn.
          </p>
        </div>

        {/* Body */}
        <article className="prose prose-invert prose-zinc max-w-none space-y-5 text-[15px] md:text-base leading-relaxed">
          <p className="text-zinc-300 text-lg md:text-xl leading-relaxed">
            AUDIVUE is a home for AI music videos — made to be watched, and
            made for the people making them. Think Vevo and Spotify, with a
            little TikTok in the mix.
          </p>

          <Section title="How it started">
            <p>It started with a blank screen.</p>
            <p>
              MTV used to <em>be</em> music videos — all day, all night. The
              music was the show. Then it walked away from that, and the
              screen went blank.
            </p>
            <p>
              I noticed it one night with friends. Good music playing, and a
              TV on the wall with nothing on it. I didn&apos;t want background
              noise or something to half-watch. I just wanted that blank space
              on the wall filled with art and music — something to look at
              while we listened.
            </p>
            <p>
              That was about five years ago. The technology to make it
              didn&apos;t exist yet, so I waited. Now it does — anyone with a
              song and an idea can turn it into something you watch. AUDIVUE
              is here to fill the screen.
            </p>
          </Section>

          <Section title="No gatekeepers">
            <p>
              For as long as music videos have existed, getting one seen meant
              getting past someone. A label. A publisher. A distributor. A
              budget most artists never had.
            </p>
            <p className="text-white font-semibold">That&apos;s over.</p>
            <p>
              You don&apos;t need a record deal to make a music video now —
              and you don&apos;t need one to share it. No middleman, no
              permission, no one deciding whether your work deserves an
              audience. You make it, you post it, it&apos;s live. Just the
              work, and the people who want to see it.
            </p>
          </Section>

          <Section title="What you'll find here">
            <p>
              If you listen, it&apos;s built around what you actually want to
              see and hear — with Pulse for quick discovery and a library
              that&apos;s yours.
            </p>
            <p>
              If you create, it&apos;s a real home for your work, not a place
              that buries it. You post, you get found, and the earliest
              creators help shape what this becomes.
            </p>
            <p className="text-white font-semibold">
              If you make AI music videos, this is for you.
            </p>
          </Section>

          <Section title="Who I am">
            <figure className="mt-2 mb-4 overflow-hidden rounded-2xl border border-zinc-800/80 shadow-2xl">
              <img
                src="/founder-courtney.jpg"
                alt="Courtney — founder of AUDIVUE — in her studio"
                loading="lazy"
                className="w-full h-auto object-cover"
                data-testid="about-founder-photo"
              />
              <figcaption className="px-4 py-3 text-xs uppercase tracking-[0.2em] text-zinc-500 bg-zinc-950/60">
                Courtney · Founder, AUDIVUE
              </figcaption>
            </figure>
            <p>
              I&apos;m Courtney. I went to Berklee College of Music, and I sing.
            </p>
            <p>
              I built AUDIVUE because it should exist — a place where anyone
              with music can put it on the screen and be seen. I made the
              stage I always wanted, and it&apos;s open to everyone.
            </p>
          </Section>

          <Section title="Get in touch">
            <p>
              Creators, press, or just to say hi — reach me at{' '}
              <a
                href="mailto:courtney@audivue.io"
                className="text-orange-400 hover:text-orange-300 underline"
                data-testid="about-contact-email"
              >
                courtney@audivue.io
              </a>
              .
            </p>
          </Section>

          <div className="pt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Link
              to="/pulse"
              className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-orange-500 hover:bg-orange-400 text-black font-semibold transition-colors"
              data-testid="about-cta-pulse"
            >
              Open Pulse
            </Link>
            <Link
              to="/become-a-creator"
              className="inline-flex items-center justify-center px-6 py-3 rounded-full border border-zinc-700 hover:border-zinc-500 text-zinc-200 font-semibold transition-colors"
              data-testid="about-cta-creator"
            >
              Become a creator
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className="text-xl md:text-2xl font-semibold text-white mt-10">{title}</h2>
    {typeof children === 'string' ? <p>{children}</p> : children}
  </section>
);

export default AboutPage;
